/**
 * Logger utility for production-safe logging
 *
 * Aligned with BT Pay Checkout UI logging standards:
 * - Development: Logs all levels (debug, info, warn, error) to console
 * - Production: Only error level is logged
 * - Structured logging with contextual metadata
 * - Service name prefix for identification: [AgentProgressUI]
 *
 * Based on BT Pay Checkout UI logger pattern (uses AWS Lambda Powertools on backend)
 */

// Browser-safe development detection
// In production builds, bundlers typically replace import.meta.env or set global flags
const isDevelopment = import.meta.env?.DEV ?? false;

// Service name for log identification (aligned with BT Pay pattern)
const SERVICE_NAME = 'AgentProgressUI';

export const logger = {
  /**
   * Debug level logging (development only)
   * Use for detailed diagnostic information
   */
  debug: (message: string, context?: unknown): void => {
    if (isDevelopment) {
      console.debug(`[${SERVICE_NAME}] ${message}`, context);
    }
  },

  /**
   * Info level logging (development only)
   * Use for general operational information
   */
  info: (message: string, context?: unknown): void => {
    if (isDevelopment) {
      console.log(`[${SERVICE_NAME}] ${message}`, context);
    }
  },

  /**
   * Warning level logging (development only)
   * Use for warning conditions (validation issues, edge cases)
   */
  warn: (message: string, context?: unknown): void => {
    if (isDevelopment) {
      console.warn(`[${SERVICE_NAME}] ${message}`, context);
    }
  },

  /**
   * Error level logging (always logged, even in production)
   * In production, errors should be sent to error tracking service
   *
   * @param message - Error message (action-oriented, e.g., "Failed to send field event")
   * @param error - Error object or error details
   * @param context - Additional contextual metadata (paymentId, sessionId, etc.)
   */
  error: (message: string, error?: unknown, context?: unknown): void => {
    console.error(`[${SERVICE_NAME}] ${message}`, error, context);

    // TODO: Integrate with error tracking service in production
    // BT Pay Checkout UI uses AWS Lambda Powertools → CloudWatch
    // Frontend should use Sentry or similar service
    //
    // Implementation placeholder:
    // if (!isDevelopment && typeof window !== 'undefined' && window.Sentry) {
    //   window.Sentry.captureException(error, {
    //     extra: { message, context }
    //   });
    // }
  },
};
